// SmartNet — catalogo dos tipos de no do construtor de fluxo. Uma unica
// fonte de verdade pra paleta, pro mapeamento node-types do Vue Flow e pro
// valor padrao de "data" de cada tipo quando um no novo e criado.
//
// Os `type` aqui tem que bater exatamente com as chaves de EXECUTOR_MAP em
// app/services/bot_flows/engine_service.rb no backend.
export const NODE_TYPES = {
  start: {
    type: 'start',
    label: 'Início',
    icon: 'i-lucide-play',
    color: '#10b981',
    singleton: true,
    defaultData: () => ({}),
  },
  message: {
    type: 'message',
    label: 'Mensagem',
    icon: 'i-lucide-message-circle',
    color: '#3b82f6',
    defaultData: () => ({ text: '' }),
  },
  question: {
    type: 'question',
    label: 'Pergunta',
    icon: 'i-lucide-help-circle',
    color: '#8b5cf6',
    defaultData: () => ({ text: '', mode: 'texto', variable: '', options: [] }),
  },
  condition: {
    type: 'condition',
    label: 'Condição',
    icon: 'i-lucide-git-branch',
    color: '#f59e0b',
    defaultData: () => ({ variable: '', operator: 'equal_to', value: '' }),
  },
  hubsoft_consultar_fatura: {
    type: 'hubsoft_consultar_fatura',
    label: 'Consultar Fatura (HubSoft)',
    icon: 'i-lucide-file-text',
    color: '#06b6d4',
    defaultData: () => ({}),
  },
  hubsoft_consultar_planos: {
    type: 'hubsoft_consultar_planos',
    label: 'Consultar Planos (HubSoft)',
    icon: 'i-lucide-list',
    color: '#0891b2',
    defaultData: () => ({}),
  },
  transfer_human: {
    type: 'transfer_human',
    label: 'Transferir Humano',
    icon: 'i-lucide-user-round',
    color: '#ef4444',
    defaultData: () => ({ label: '', team_id: null }),
  },
  ask_jhulie: {
    type: 'ask_jhulie',
    label: 'Perguntar à IA',
    icon: 'i-lucide-sparkles',
    color: '#ec4899',
    defaultData: () => ({
      goal: '',
      allowed_tools: [],
      success_criteria: '',
      handoff_criteria: '',
      fallback_message: '',
    }),
  },
  identify_intent: {
    type: 'identify_intent',
    label: 'Identificar Intenção',
    icon: 'i-lucide-brain-circuit',
    color: '#a855f7',
    defaultData: () => ({ intents: [] }),
  },
  http_request: {
    type: 'http_request',
    label: 'Requisição HTTP',
    icon: 'i-lucide-globe',
    color: '#0ea5e9',
    defaultData: () => ({ url: '', method: 'get', headers: [], body: '', save_response_as: '', save_status_as: '' }),
  },
  sub_flow: {
    type: 'sub_flow',
    label: 'Executar Fluxo',
    icon: 'i-lucide-workflow',
    color: '#14b8a6',
    defaultData: () => ({ flow_id: null }),
  },
  delay: {
    type: 'delay',
    label: 'Espera',
    icon: 'i-lucide-clock',
    color: '#eab308',
    defaultData: () => ({ seconds: 2 }),
  },
  end: {
    type: 'end',
    label: 'Fim',
    icon: 'i-lucide-flag',
    color: '#64748b',
    defaultData: () => ({}),
  },
};

export const PALETTE_ITEMS = Object.values(NODE_TYPES).filter(n => !n.singleton);

// SmartNet — ferramentas de dominio reais que o no "Perguntar a IA" pode
// restringir (ver lib/smartnet/tool_guard.rb#flow_scoped_decision e
// enterprise/app/models/captain/assistant.rb#hubsoft_tools). So aparecem
// aqui as que realmente existem e estao registradas na politica
// (config/smartnet_tool_policies.yml) - ferramentas de sistema (FAQ,
// transferir, etiqueta, CSAT) sempre ficam disponiveis e nao aparecem nesta
// lista porque nao podem ser restringidas por um fluxo.
export const FLOW_RESTRICTABLE_TOOLS = [
  { value: 'captain--tools--hubsoft_consultar_fatura', label: 'Consultar fatura (HubSoft)' },
  { value: 'captain--tools--hubsoft_vincular_cliente', label: 'Vincular cliente por CPF (HubSoft)' },
  { value: 'captain--tools--hubsoft_consultar_planos', label: 'Consultar planos e preços (HubSoft)' },
];

export const CONDITION_OPERATORS = [
  { value: 'equal_to', label: 'É igual a' },
  { value: 'not_equal_to', label: 'É diferente de' },
  { value: 'contains', label: 'Contém' },
  { value: 'does_not_contain', label: 'Não contém' },
  { value: 'starts_with', label: 'Começa com' },
  { value: 'is_present', label: 'Está preenchida' },
  { value: 'is_not_present', label: 'Está vazia' },
  { value: 'is_greater_than', label: 'É maior que (número)' },
  { value: 'is_less_than', label: 'É menor que (número)' },
];

let nodeIdCounter = 0;
export const generateNodeId = () => {
  nodeIdCounter += 1;
  return `node_${Date.now()}_${nodeIdCounter}`;
};

// SmartNet — mesmas regras de app/services/bot_flows/graph_validator.rb,
// portadas pro editor pra avisar o usuario antes de ele sair da tela (a
// validacao que de fato BLOQUEIA a ativacao roda no backend, esta aqui e so
// um aviso cedo). Mantenha as duas em sincronia se mudar uma.
const SINGLE_NEXT_TYPES = [
  'start',
  'message',
  'question',
  'hubsoft_consultar_fatura',
  'identify_intent',
  'http_request',
  'delay',
];

export const validateFlowGraph = (nodes, edges) => {
  const issues = [];
  const outgoing = nodeId => edges.filter(e => e.source === nodeId);
  const label = node => (node.data && node.data.text) || NODE_TYPES[node.type]?.label || node.type;

  nodes
    .filter(n => SINGLE_NEXT_TYPES.includes(n.type))
    .forEach(node => {
      if (outgoing(node.id).length === 0) {
        issues.push(`"${label(node)}" não está conectado a um próximo passo.`);
      }
    });

  nodes
    .filter(n => n.type === 'condition')
    .forEach(node => {
      const handles = outgoing(node.id).map(e => e.sourceHandle);
      const missing = [];
      if (!handles.includes('true')) missing.push('verdadeiro');
      if (!handles.includes('false')) missing.push('falso');
      if (missing.length) {
        issues.push(`Condição "${label(node)}" está sem o caminho: ${missing.join(' e ')}.`);
      }
    });

  return issues;
};
